// Service for fetching comprehensive token market data from Solana Tracker API
interface SolanaTrackerTokenResponse {
    token: {
        name: string;
        symbol: string;
        mint: string;
        uri: string;
        decimals: number;
        description: string;
        image: string;
        showName: boolean;
        createdOn: string;
        twitter?: string;
        telegram?: string;
        website?: string;
        discord?: string;
        youtube?: string;
        hasFileMetaData: boolean;
        strictSocials?: {
            twitter?: string;
            telegram?: string;
            website?: string;
            discord?: string;
            youtube?: string;
        };
        creation: {
            creator: string;
            created_tx: string;
            created_time: number;
        };
    };
    pools: Array<{
        poolId: string;
        liquidity: {
            quote: number;
            usd: number;
        };
        price: {
            quote: number;
            usd: number;
        };
        tokenSupply: number;
        lpBurn?: number;
        tokenAddress: string;
        marketCap: {
            quote: number;
            usd: number;
        };
        market: string;
        quoteToken: string;
        decimals: number;
        security: {
            freezeAuthority: string | null;
            mintAuthority: string | null;
        };
        deployer: string;
        lastUpdated: number;
        createdAt?: number;
        txns: {
            sells: number;
            total: number;
            volume: number;
            buys: number;
        };
    }>;
    events: {
        [key: string]: {
            priceChangePercentage: number;
        };
    };
    risk: {
        rugged: boolean;
        risks: Array<{
            name: string;
            description: string;
            value: string;
            level: string;
            score: number;
        }>;
        score: number;
    };
    buys: number;
    sells: number;
    txns: number;
    holders: number;
}

export interface ProcessedTokenMarketData {
    // Basic token info
    name: string;
    symbol: string;
    mint: string;
    image: string;
    description: string;

    // Price and market data
    price: {
        usd: number;
        quote: number;
    };
    liquidity: {
        usd: number;
        quote: number;
    };
    marketCap: {
        usd: number;
        quote: number;
    };
    allTimeVolume: number;

    // Market info
    market: string;
    tokenSupply: number;
    lpBurn: number;

    // Price changes
    priceChange: {
        '1h': number;
        '24h': number;
    };

    // Social links
    socials: {
        twitter?: string;
        website?: string;
        telegram?: string;
        discord?: string;
        youtube?: string;
    };

    // Security
    security: {
        freezeAuthority: string | null;
        mintAuthority: string | null;
    };

    // Additional metrics
    holders: number;
    riskScore: number;
    isRugged: boolean;

    // Metadata
    lastUpdated: number;
    createdAt?: number;
}

class TokenMarketDataService {
    private readonly apiKey: string;
    private readonly baseUrl = 'https://data.solanatracker.io';
    private cache = new Map<string, { data: ProcessedTokenMarketData; timestamp: number }>();
    private readonly cacheTimeout = 30000; // 30 seconds cache

    constructor() {
        this.apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
        if (!this.apiKey) {
            console.error('VITE_SOLANA_TRACKER_API_KEY is not set in .env file');
        }
    }

    /**
     * Fetch comprehensive token market data from Solana Tracker API
     */
    async fetchTokenMarketData(tokenAddress: string): Promise<ProcessedTokenMarketData | null> {
        if (!this.apiKey) {
            console.error('Solana Tracker API key not configured');
            return null;
        }

        // Check cache first
        const cached = this.cache.get(tokenAddress);
        if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
            console.log(`📋 TokenMarketDataService: Using cached data for ${tokenAddress}`);
            return cached.data;
        }

        try {
            console.log(`📡 TokenMarketDataService: Fetching market data for ${tokenAddress}`);

            const response = await fetch(`${this.baseUrl}/tokens/${tokenAddress}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${this.apiKey}`,
                    'X-API-Key': this.apiKey,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                console.error(`❌ TokenMarketDataService: API request failed with status ${response.status}`);
                return null;
            }

            const data: SolanaTrackerTokenResponse = await response.json();
            console.log(`✅ TokenMarketDataService: Received data for ${tokenAddress}:`, data);

            // Process the data
            const processedData = this.processTokenData(data);

            // Cache the processed data
            this.cache.set(tokenAddress, {
                data: processedData,
                timestamp: Date.now()
            });

            return processedData;
        } catch (error) {
            console.error(`❌ TokenMarketDataService: Error fetching data for ${tokenAddress}:`, error);
            return null;
        }
    }

    /**
     * Process raw API response into our standardized format
     */
    private processTokenData(data: SolanaTrackerTokenResponse): ProcessedTokenMarketData {
        // Find the best pool (highest liquidity)
        const bestPool = data.pools
            .filter(pool => pool.liquidity.usd > 0 && pool.price.usd > 0)
            .sort((a, b) => b.liquidity.usd - a.liquidity.usd)[0];

        if (!bestPool) {
            throw new Error('No valid pools found for token');
        }

        // Calculate total all-time volume across all pools
        const allTimeVolume = data.pools.reduce((sum, pool) => sum + (pool.txns?.volume || 0), 0);

        console.log(`📊 TokenMarketDataService: All-time volume across all pools: $${allTimeVolume.toFixed(2)}`);

        // Extract social links (prefer strictSocials over direct fields)
        const socials: ProcessedTokenMarketData['socials'] = {};

        if (data.token.strictSocials?.twitter || data.token.twitter) {
            socials.twitter = data.token.strictSocials?.twitter || data.token.twitter;
        }
        if (data.token.strictSocials?.website || data.token.website) {
            socials.website = data.token.strictSocials?.website || data.token.website;
        }
        if (data.token.strictSocials?.telegram || data.token.telegram) {
            socials.telegram = data.token.strictSocials?.telegram || data.token.telegram;
        }
        if (data.token.strictSocials?.discord || data.token.discord) {
            socials.discord = data.token.strictSocials?.discord || data.token.discord;
        }
        if (data.token.strictSocials?.youtube || data.token.youtube) {
            socials.youtube = data.token.strictSocials?.youtube || data.token.youtube;
        }

        return {
            // Basic token info
            name: data.token.name,
            symbol: data.token.symbol,
            mint: data.token.mint,
            image: data.token.image,
            description: data.token.description,

            // Price and market data from best pool
            price: {
                usd: bestPool.price.usd,
                quote: bestPool.price.quote
            },
            liquidity: {
                usd: bestPool.liquidity.usd,
                quote: bestPool.liquidity.quote
            },
            marketCap: {
                usd: bestPool.marketCap.usd,
                quote: bestPool.marketCap.quote
            },
            allTimeVolume,

            // Market info
            market: bestPool.market,
            tokenSupply: bestPool.tokenSupply,
            lpBurn: bestPool.lpBurn || 0,

            // Price changes
            priceChange: {
                '1h': data.events['1h']?.priceChangePercentage || 0,
                '24h': data.events['24h']?.priceChangePercentage || 0
            },

            // Social links
            socials,

            // Security
            security: {
                freezeAuthority: bestPool.security.freezeAuthority,
                mintAuthority: bestPool.security.mintAuthority
            },

            // Additional metrics
            holders: data.holders || 0,
            riskScore: data.risk?.score || 0,
            isRugged: data.risk?.rugged || false,

            // Metadata
            lastUpdated: bestPool.lastUpdated,
            createdAt: bestPool.createdAt
        };
    }

    /**
     * Clear cache for a specific token
     */
    clearCache(tokenAddress?: string): void {
        if (tokenAddress) {
            this.cache.delete(tokenAddress);
        } else {
            this.cache.clear();
        }
    }

    /**
     * Get cache status
     */
    getCacheInfo(): { size: number; tokens: string[] } {
        return {
            size: this.cache.size,
            tokens: Array.from(this.cache.keys())
        };
    }
}

// Export singleton instance
export const tokenMarketDataService = new TokenMarketDataService();
export default tokenMarketDataService; 